/* What a support session looks like from inside the console.
 *
 * A platform admin can start a support session from the admin app — a row in
 * `admin_impersonation` that makes the tenant lookup every RLS policy runs
 * answer with the salon they are supporting rather than their own membership.
 * See admin/server/schema/13-impersonation.sql. The console then loads that
 * salon's board with nothing else changed, which is the point of the design and
 * also the whole problem: the screen is a customer's business and looks exactly
 * like one's own.
 *
 * So the console has to say so, and it has to say two different things:
 *
 *   live    the session is running. Show which salon and how long is left, and
 *           get louder as it runs out.
 *   ended   it expired, or somebody stopped it from the admin app. The banner
 *           does NOT go away at that point, and that is deliberate — expiry ends
 *           the tenant RESOLUTION, not a platform admin's right to write any
 *           salon, so an open tab carries on saving into the customer's day. The
 *           only thing that ends is the part that was making it obvious.
 *
 * Pure and tested for the usual reason: the boundaries — the last minute, a
 * expiry already in the past, a garbled timestamp — are what this is for, and
 * they are the parts nobody exercises by hand.
 *
 * The countdown mirrors admin/js/rules/support-session.js, on purpose and not by
 * import: the two apps are separate builds with separate dependency trees, and
 * a shared module between them would be a build-order coupling for forty lines
 * of arithmetic. They must agree on the wording, and the tests on both sides are
 * what keeps them agreeing.
 */

/* The banner, as something to render.
 *
 *   session   { tenantName, expiresAt, ended } as sync.js keeps it, or null
 *   nowMs     the clock, passed in so this stays pure
 *
 * `ended` on the session is set when a re-check comes back empty after this tab
 * has seen a live one — a session that was stopped by hand looks like nothing at
 * all from the database's side, so the tab has to remember it had one.
 */
export function supportBanner(session, nowMs) {
  if (!session || !session.tenantName) {
    return { show: false, ended: false, tenantName: null, label: '', urgent: false };
  }

  var base = { show: true, ended: true, tenantName: session.tenantName, label: '', urgent: true };
  if (session.ended) return base;

  /* `!(ms > 0)` rather than `ms <= 0`, so an unparseable expiry — NaN — reads as
   * ended too. The alternative is a banner that says a session is live for ever
   * because the one field it depends on is broken, which is the failure this
   * whole file exists to prevent. */
  var ms = new Date(session.expiresAt).getTime() - nowMs;
  if (!(ms > 0)) return base;

  /* Counts DOWN through minutes rather than switching to hours early. At 90
   * minutes "1h 30m" is fine; at 4 minutes "4m" is the entire point, and there is
   * no rounding of "about an hour" that helps somebody whose session is about to
   * stop being visible. */
  var minutes = Math.floor(ms / 60000);
  var label;
  if (minutes < 1) label = 'under a minute';
  else if (minutes < 60) label = minutes + 'm';
  else {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    label = m ? h + 'h ' + m + 'm' : h + 'h';
  }

  // Under five minutes is always nearly over: five is the shortest session the
  // database will start, so nothing that reads under it is merely short.
  return {
    show: true,
    ended: false,
    tenantName: session.tenantName,
    label: label,
    urgent: minutes < 5
  };
}
